import React from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import axios from '../axiosClient.js';
import Alert from './common/Alert.jsx';
import PageLoading from './common/PageLoading.jsx';
import QuotaBreakdownHeader from './QuotaBreakdownHeader.jsx';
import QuotaBreakdownRow from './QuotaBreakdownRow.jsx';

const REFRESH_INTERVAL_MS = 300_000;

function QuotaBreakdownTable({ selectedRep, selectedYear, enabled = true }) {
  const [alertError, setAlertError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [quotaBreakdown, setQuotaBreakdown] = React.useState([]);

  // Fetch explicitly on enable/selection; refresh only after the last request settles.
  React.useEffect(() => {
    const controller = new AbortController();
    let timer;
    setLoading(true);
    setAlertError(null);
    setQuotaBreakdown([]);

    const fetchSummary = async () => {
      try {
        const { data } = await axios.get(
          `/api/quota-breakdown/${encodeURIComponent(selectedRep)}/${selectedYear}`,
          { signal: controller.signal, timeout: 60000 }
        );
        if (controller.signal.aborted) return;
        setQuotaBreakdown(data);
        setAlertError(null);
      } catch (err) {
        if (controller.signal.aborted) return;
        setAlertError(err.response?.data?.error || err.message);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          timer = setTimeout(fetchSummary, REFRESH_INTERVAL_MS);
        }
      }
    };

    if (enabled && selectedRep && selectedYear) fetchSummary();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [selectedRep, selectedYear, enabled]);

  return (
    <Box>
      <Typography data-cy="quota-breakdown-header" variant="h5" sx={{ textAlign: 'center' }}>
        Quota Breakdown
      </Typography>
      <Alert
        data-cy="quota-breakdown-alert"
        message={alertError}
        setMessage={setAlertError}
        level="error"
      />
      {loading ? (
        <PageLoading />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <QuotaBreakdownHeader />
            <TableBody>
              {quotaBreakdown.length ? (
                quotaBreakdown.map((quota) => (
                  <QuotaBreakdownRow key={quota.CATEGORY} quota={quota} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan="20" style={{ textAlign: 'center', padding: '1rem' }}>
                    No quota data set for {selectedRep}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default React.memo(QuotaBreakdownTable);
